import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const MOCK_SITES = [
  {
    name: 'Morrison Home Farm',
    totalAcres: 1240,
    fields: [
      { id: 'F-001', name: 'North Quarter', acres: 156, crop: 'Corn', status: 'planting', progress: 87, soil: 'Silt Loam', lastOp: 'Planting - 87% complete' },
      { id: 'F-002', name: 'South 160', acres: 160, crop: 'Soybeans', status: 'scheduled', progress: 0, soil: 'Clay Loam', lastOp: 'Tillage completed 2/5' },
      { id: 'F-003', name: 'East Bottoms', acres: 220, crop: 'Corn', status: 'completed', progress: 100, soil: 'Sandy Loam', lastOp: 'Tillage completed today' },
      { id: 'F-004', name: 'West 80', acres: 80, crop: 'Corn', status: 'spraying', progress: 53, soil: 'Silt Loam', lastOp: 'Herbicide application - 53%' },
      { id: 'F-005', name: 'River Field', acres: 160, crop: 'Soybeans', status: 'sampling', progress: 56, soil: 'Alluvial', lastOp: 'Soil sampling - 56%' },
    ],
  },
  {
    name: 'Hutchins Lease',
    totalAcres: 480,
    fields: [
      { id: 'F-010', name: 'Hutch North', acres: 160, crop: 'Corn', status: 'scheduled', progress: 0, soil: 'Silt Loam', lastOp: 'Awaiting planting' },
      { id: 'F-011', name: 'Hutch South', acres: 160, crop: 'Soybeans', status: 'scheduled', progress: 0, soil: 'Clay Loam', lastOp: 'Awaiting tillage' },
      { id: 'F-012', name: 'Hutch West', acres: 160, crop: 'Corn', status: 'tillage', progress: 30, soil: 'Silt Loam', lastOp: 'Tillage in progress' },
    ],
  },
];

function FieldsPage() {
  const [sites] = useState(MOCK_SITES);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => { /* Future: fetch from Supabase */ }, []);

  const getStatusBadge = (status) => {
    const map = { planting: 'badge-success', completed: 'badge-default', scheduled: 'badge-warning', spraying: 'badge-success', sampling: 'badge-success', tillage: 'badge-success' };
    return map[status] || 'badge-default';
  };

  const getProgressColor = (pct) => {
    if (pct >= 100) return '#367C2B';
    if (pct >= 50) return '#4A90D9';
    if (pct > 0) return '#FFDE00';
    return 'rgba(255,255,255,0.2)';
  };

  const allFields = sites.flatMap(s => s.fields);
  const totalAcres = allFields.reduce((sum, f) => sum + f.acres, 0);
  const statuses = ['all', ...new Set(allFields.map(f => f.status))];

  return (
    <div className="page-body">
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0 }}>Fields</h1>
          <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '0.85rem' }}>{allFields.length} fields across {sites.length} sites &mdash; {totalAcres.toLocaleString()} total acres</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder="Search fields..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.15)", background: "var(--surface)", color: "var(--text)", fontSize: "0.9rem", minWidth: "200px" }}
        />
        {statuses.map(s => (
          <button key={s}
            className={filterStatus === s ? "btn btn-primary" : "btn btn-secondary"}
            onClick={() => setFilterStatus(s)}
            style={{ textTransform: 'capitalize' }}
          >{s}</button>
        ))}
      </div>

      {sites.map((site) => {
        const filtered = site.fields.filter(f => {
          const matchSearch = !searchTerm || f.name.toLowerCase().includes(searchTerm.toLowerCase()) || f.crop.toLowerCase().includes(searchTerm.toLowerCase());
          const matchStatus = filterStatus === 'all' || f.status === filterStatus;
          return matchSearch && matchStatus;
        });
        if (filtered.length === 0) return null;
        return (
          <div className="card" key={site.name} style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h3 style={{ margin: 0 }}>{site.name}</h3>
              <span style={{ fontSize: '0.85rem', opacity: 0.6 }}>{site.totalAcres.toLocaleString()} acres</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "12px" }}>
              {filtered.map((field) => (
                <div key={field.id} style={{ background: "rgba(255,255,255,0.03)", borderRadius: "8px", padding: "14px", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{field.name}</div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.5, fontFamily: 'monospace' }}>{field.id}</div>
                    </div>
                    <span className={getStatusBadge(field.status)} style={{ textTransform: 'capitalize' }}>{field.status}</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", fontSize: "0.8rem", opacity: 0.7, marginBottom: "10px" }}>
                    <div>Crop: <strong>{field.crop}</strong></div>
                    <div>Acres: <strong>{field.acres}</strong></div>
                    <div>Soil: <strong>{field.soil}</strong></div>
                    <div>Last: <strong>{field.lastOp}</strong></div>
                  </div>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", marginBottom: "4px" }}>
                      <span>Progress</span><span>{field.progress}%</span>
                    </div>
                    <div style={{ width: "100%", height: "6px", background: "rgba(255,255,255,0.1)", borderRadius: "3px" }}>
                      <div style={{ width: field.progress + "%", height: "100%", borderRadius: "3px", background: getProgressColor(field.progress) }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default FieldsPage;